import { forwardRef, useImperativeHandle, useState, useMemo, useEffect } from 'react';
import { Form, message, Modal, Spin, Descriptions } from "antd";
import { useRequest } from "ahooks";
import { EditForm } from "../../../components/editForm";
import type { formItemProps } from "../../../components/editForm/data";
import { AmountServer } from "../../../server";

export type FormConfigType = formItemProps[];

const server = AmountServer.InternationalRemittanceSlippageList;

const DEFAULT_OPERATION_DATA = {
    ChannelId: undefined,
    CountryId: undefined,
    Slippage: '',
}

type OperateModeType = 'create' | 'update'

export const Operation = forwardRef((props: any, ref: any) => {
    const { onSuccess, options } = props;
    const [loading, setLoading] = useState<boolean>(false);
    const [visible, setVisiable] = useState<boolean>(false);
    const [formData, setFormData] = useState<any>({ ...DEFAULT_OPERATION_DATA });
    const [form] = Form.useForm();
    const [mode, setMode] = useState<OperateModeType>('create');
    const [exchangeRate, setExchangeRate] = useState<string>('');
    const [exchangeRateLoading, setExchangeRateLoading] = useState<boolean>(false);

    useImperativeHandle(ref, () => ({
        changeVisible
    }))

    const changeVisible = (val: any, mode?: OperateModeType) => {
        if (val) {
            const data = {
                ...DEFAULT_OPERATION_DATA,
                ...(mode === 'update' ? val : {})
            };
            mode && setMode(mode)
            form?.resetFields?.();
            setFormData(data);
            
            // 如果是编辑模式，获取汇率信息
            if (mode === 'update' && val?.Channel?.Id && val?.Country?.Id) {
                fetchExchangeRate(val.Channel.Id, val.Country.Id);
            }
        } else {
            form?.resetFields?.();
            setExchangeRate('');
        }
        setVisiable(!!val);
    }

    // 获取汇率信息
    const fetchExchangeRate = async (channelId: number, countryId: number) => {
        if (!channelId || !countryId) return;
        
        setExchangeRateLoading(true);
        try {
            const res: any = await server.getExchangeRate({
                ChannelId: channelId,
                CountryId: countryId
            }); 
            setExchangeRate(res?.USDCNY || '');
        } catch (error) {
            message.error('获取汇率失败');
        } finally {
            setExchangeRateLoading(false);
        }
    }

    // 监听表单数据变化，当通道和国家都选择时获取汇率
    useEffect(() => {
        if (formData.ChannelId && formData.CountryId) {
            fetchExchangeRate(formData.ChannelId, formData.CountryId);
        }
    }, [formData.ChannelId, formData.CountryId]);

    const { run } = useRequest(mode === 'create' ? server.create : server.update, {
        manual: true,
        onBefore: () => setLoading(true),
        onFinally: () => setLoading(false),
        onError: () => changeVisible(false),
        onSuccess: (res: any) => {
            message.success(mode === 'create' ? '新增成功' : '更新成功');
            changeVisible(false);
            onSuccess?.();
        }
    });

    const getBaseInfoFormConfig: FormConfigType = useMemo((): FormConfigType => {
        if (mode === 'update') {
            return [
                {
                    title: "滑点",
                    field: "Slippage",
                    type: "input",
                    placeholder: "请输入滑点",
                    required: true,
                },
            ];
        }
        return [
            {
                title: "通道",
                field: "ChannelId",
                type: "select",
                placeholder: "请选择通道",
                options: options?.channelOptions || [],
                fieldNames: { label: 'name', value: 'id' },
                allowClear: true,
                required: true,
                onChange: (value: any) => {
                    const newFormData = { ...formData, ChannelId: value };
                    setFormData(newFormData);
                }
            },
            {
                title: "国家",
                field: "CountryId",
                type: "select",
                placeholder: "请选择国家",
                options: options?.countryOptions || [],
                fieldNames: { label: 'name', value: 'id' },
                allowClear: true,
                required: true,
                onChange: (value: any) => {
                    const newFormData = { ...formData, CountryId: value };
                    setFormData(newFormData);
                }
            },
            {
                title: "滑点",
                field: "Slippage",
                type: "input",
                placeholder: "请输入滑点",
                required: true,
            },
        ];
    }, [options, mode, formData]);

    return (
        <div>
            <Modal
                title={mode === 'create' ? '新增滑点' : '编辑滑点'}
                width={500}
                closeIcon={null}
                maskClosable={false}
                open={visible}
                onCancel={() => changeVisible(false)}
                okButtonProps={{ loading }}
                onOk={async () => {
                    await form.validateFields();
                    let submitData: any = {};
                    if (mode === 'create') {
                        submitData = {
                            ChannelId: formData.ChannelId,
                            CountryId: formData.CountryId,
                            Slippage: formData.Slippage,
                        };
                    } else {
                        submitData = {
                            Id: formData.Id,
                            Slippage: formData.Slippage,
                        };
                    }
                    run(submitData);
                }}
            >
                {/* 汇率信息展示 */}
                <Spin spinning={exchangeRateLoading}>
                    <div style={{ fontSize: '14px', marginBottom: '10px' }}>
                        实时汇率: {exchangeRate || '-'}
                    </div>
                </Spin>
                <EditForm
                    componentId="slippage-operation"
                    className="mt20"
                    form={form}
                    formData={formData}
                    onDataChange={setFormData}
                    formConfig={getBaseInfoFormConfig}
                />
            </Modal>
        </div>
    );
});